import type { ReactNode } from "react";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { Phone, MessageSquare } from "lucide-react";
import { SidebarInspectionCard, type SidebarInspectionCardProps } from "./SidebarInspectionCard";
import { ProcessSteps, type Step } from "./ProcessSteps";
import { FaqList } from "./FaqList";
import { TestimonialCard, type TestimonialCardProps } from "./TestimonialCard";
import type { Faq } from "@/lib/service-schema";
import { ManagedImage } from "@/components/site/ManagedImage";
import { ManagedProse, ManagedSection } from "@/components/site/ManagedProse";
import { PageHero } from "@/components/site/PageHero";

import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { RelatedLinks } from "@/components/site/RelatedLinks";
import { ServiceAreasStrip } from "@/components/site/ServiceAreasStrip";
import {
  RELATED_SERVICES,
  SERVICE_TO_COST,
  labelForPath,
  linkItem,
} from "@/lib/internal-links";


export type Section = { heading: string; body: ReactNode };

export interface ServicePageProps {
  /** Page slug matching the registry in src/lib/pages.ts (e.g. "foam-roofing"). */
  pageSlug?: string;
  eyebrow: string;
  h1: string;
  subhead: string;
  heroBullets?: string[];
  whatIs: Section;
  climate: Section;
  process: { heading: string; intro?: string; steps: Step[] };
  whyChoose?: Section;
  extraSection?: Section;
  /** Additional prose sections rendered after extraSection, before the FAQs. */
  extraSections?: Section[];
  faqs?: Faq[];
  /** Optional proof block rendered between the process and FAQ sections. */
  testimonial?: TestimonialCardProps;
  /** Renders the process block at the top of the body, before the intro sections. */
  processFirst?: boolean;
  /** Overrides the primary CTA button label (hero + bottom CTA). */
  ctaLabel?: string;
  ctaTitle: string;
  ctaBody: string;
  /** Overrides copy / lead tagging on the sidebar form. */
  formProps?: SidebarInspectionCardProps;
}

export function ServicePageLayout(p: ServicePageProps) {
  const slug = p.pageSlug;
  const servicePath = slug ? `/${slug}` : null;
  const relatedPaths = slug ? RELATED_SERVICES[slug] ?? [] : [];
  const relatedItems = relatedPaths.map((href) => linkItem(href));
  const costPath = slug ? SERVICE_TO_COST[slug] : undefined;
  const costItem = costPath ? linkItem(costPath) : null;
  const breadcrumbItems = servicePath
    ? [{ href: servicePath, label: labelForPath(servicePath) }]
    : [];
  const processBlock = (
    <div>
      <h2 className="text-2xl font-bold text-primary sm:text-3xl">{p.process.heading}</h2>
      {p.process.intro && (
        <p className="mt-3 max-w-3xl text-[16px] leading-relaxed text-foreground/85">
          {p.process.intro}
        </p>
      )}
      <div className="mt-6">
        <ProcessSteps steps={p.process.steps} />
      </div>
      {slug && (
        <div className="mt-6">
          <ManagedProse pageSlug={slug} sectionKey="process" className="space-y-4 text-[16px] leading-relaxed text-foreground/85" />
        </div>
      )}
    </div>
  );
  return (
    <>
      {breadcrumbItems.length > 0 && <Breadcrumbs items={breadcrumbItems} />}

      {/* Hero */}
      <PageHero
        pageSlug={slug}
        eyebrow={p.eyebrow}
        h1={p.h1}
        subhead={p.subhead}
        bullets={p.heroBullets}
        actions={
          <>
            <ButtonLink href="#inspection" variant="primary" size="lg">
              {p.ctaLabel ?? "Schedule a Free Roof Check"}
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              <span className="sm:hidden">Call Us</span>
              <span className="hidden sm:inline">480-941-5373</span>
            </ButtonLink>
            <ButtonLink href="sms:+14809415373" variant="ghost-light" size="lg" className="sm:hidden">
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              Text Us
            </ButtonLink>
          </>
        }
      />


      {/* Testimonial proof block placed right below the hero */}
      {p.testimonial && (
        <section className="bg-[var(--surface)] py-10 lg:py-12">
          <Container>
            <div className="max-w-3xl">
              <TestimonialCard {...p.testimonial} />
            </div>
          </Container>
        </section>
      )}

      {/* Two-column body */}
      <section className="bg-background py-14 lg:py-20">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px]">
            <div className="min-w-0 space-y-12">
              {p.processFirst && processBlock}
              <Block heading={p.whatIs.heading}>
                {slug ? (
                  <ManagedProse pageSlug={slug} sectionKey="intro" className="space-y-4">
                    {p.whatIs.body}
                  </ManagedProse>
                ) : (
                  p.whatIs.body
                )}
              </Block>

              {slug && (
                <ManagedImage
                  pageSlug={slug}
                  slot="content-1"
                  variant="content"
                />
              )}

              <Block heading={p.climate.heading}>
                {slug ? (
                  <ManagedProse pageSlug={slug} sectionKey="climate" className="space-y-4">
                    {p.climate.body}
                  </ManagedProse>
                ) : (
                  p.climate.body
                )}
              </Block>

              {slug && <ManagedSection pageSlug={slug} sectionKey="main" heading="In our experience" />}

              {!p.processFirst && processBlock}

              {slug && (
                <ManagedImage
                  pageSlug={slug}
                  slot="content-2"
                  variant="content"
                />
              )}

              {p.whyChoose && (
                <Block heading={p.whyChoose.heading}>
                  {slug ? (
                    <ManagedProse pageSlug={slug} sectionKey="why_us" className="space-y-4">
                      {p.whyChoose.body}
                    </ManagedProse>
                  ) : (
                    p.whyChoose.body
                  )}
                </Block>
              )}

              {p.extraSection && <Block heading={p.extraSection.heading}>{p.extraSection.body}</Block>}

              {p.extraSections?.map((s) => (
                <Block key={s.heading} heading={s.heading}>
                  {s.body}
                </Block>
              ))}

              {p.faqs && p.faqs.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-primary sm:text-3xl">
                    Frequently asked questions
                  </h2>
                  <div className="mt-6">
                    <FaqList faqs={p.faqs} />
                  </div>
                </div>
              )}
            </div>

            <div id="inspection" className="lg:relative">
              <div className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-1">
                <SidebarInspectionCard {...(p.formProps ?? {})} />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Internal-link sections */}
      {(relatedItems.length > 0 || costItem) && (
        <section className="bg-[var(--surface)] py-14 lg:py-16">
          <Container className="space-y-10">
            {relatedItems.length > 0 && (
              <RelatedLinks heading="Related services" links={relatedItems} />
            )}
            {costItem && (
              <RelatedLinks
                heading="What does this cost?"
                intro="Real Phoenix price ranges, written plainly."
                links={[costItem]}
                columns={2}
                seeAll={{ href: "/roofing-costs-phoenix", label: "See every Phoenix roofing cost guide" }}
              />
            )}
            <ServiceAreasStrip />
          </Container>
        </section>
      )}


      {/* Bottom CTA */}
      <section className="bg-[var(--primary-deep)] py-14 text-white lg:py-16">
        <Container className="text-center">
          <h2 className="mx-auto max-w-3xl text-2xl font-bold text-white sm:text-3xl">{p.ctaTitle}</h2>
          <p className="mx-auto mt-3 max-w-2xl text-white/80">{p.ctaBody}</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink href="#inspection" variant="primary" size="lg">
              {p.ctaLabel ?? "Schedule a Free Roof Check"}
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              <span className="sm:hidden">Call Us</span>
              <span className="hidden sm:inline">Call 480-941-5373</span>
            </ButtonLink>
            <ButtonLink href="sms:+14809415373" variant="ghost-light" size="lg" className="sm:hidden">
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              Text Us
            </ButtonLink>
          </div>
        </Container>
      </section>
    </>
  );
}

function Block({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-primary sm:text-3xl">{heading}</h2>
      <div className="prose-content mt-4 space-y-4 text-[16px] leading-relaxed text-foreground/85 [&_p]:m-0 [&_ul]:m-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_li]:text-foreground/85">
        {children}
      </div>
    </div>
  );
}
